export type ConversationLeadStatus =
  | 'new'
  | 'registered'
  | 'interested'
  | 'qualified'
  | 'scheduled'
  | 'quoted'
  | 'converted'
  | 'lost';

export interface ConversationLeadStatusDefinition {
  value: ConversationLeadStatus;
  label: string;
  color: string;
  description: string;
}

export interface ConversationLeadStatusBand extends ConversationLeadStatusDefinition {
  exactCount: number;
  cumulativeCount: number;
}

export type ConversationLossReason =
  | 'delivery_failed'
  | 'no_engagement'
  | 'abandoned'
  | 'declined';

export interface ConversationLossReasonDefinition {
  value: ConversationLossReason | 'unclassified';
  label: string;
  color: string;
  description: string;
}

export interface ConversationLossReasonBand extends ConversationLossReasonDefinition {
  count: number;
}

export interface ConversationLeadStatusFunnelData {
  mainBands: ConversationLeadStatusBand[];
  sideBand: ConversationLeadStatusBand;
  lostReasonBands: ConversationLossReasonBand[];
  declineReasonBands: Array<{ label: string; count: number }>;
}

export const mainFunnelStatuses: ConversationLeadStatus[] = [
  'new',
  'registered',
  'interested',
  'qualified',
  'scheduled',
  'quoted',
  'converted',
];

export const conversationLeadStatusDefinitions: ConversationLeadStatusDefinition[] = [
  {
    value: 'new',
    label: 'Nuevo',
    color: '#2563eb',
    description: 'Conversaciones recién iniciadas, todavía sin datos clave de avance.',
  },
  {
    value: 'registered',
    label: 'Registrado',
    color: '#0891b2',
    description: 'Lead identificado y registrado con datos básicos en la conversación.',
  },
  {
    value: 'interested',
    label: 'Interesado',
    color: '#ea580c',
    description: 'Lead con interés explícito en un proyecto o producto.',
  },
  {
    value: 'qualified',
    label: 'Calificado',
    color: '#16a34a',
    description: 'Lead con señales suficientes para seguimiento comercial prioritario.',
  },
  {
    value: 'scheduled',
    label: 'Programado',
    color: '#7c3aed',
    description: 'Lead con cita o siguiente paso comercial ya agendado.',
  },
  {
    value: 'quoted',
    label: 'Cotizó',
    color: '#0d9488',
    description: 'Lead que llegó a cotización de unidad o sala de ventas.',
  },
  {
    value: 'converted',
    label: 'Convertido',
    color: '#15803d',
    description: 'Lead que completó la conversión esperada en el embudo.',
  },
  {
    value: 'lost',
    label: 'Perdido',
    color: '#dc2626',
    description: 'Lead que salió del proceso comercial sin conversión.',
  },
];

export const conversationLeadStatusMap = conversationLeadStatusDefinitions.reduce<
  Record<ConversationLeadStatus, ConversationLeadStatusDefinition>
>((accumulator, definition) => {
  accumulator[definition.value] = definition;
  return accumulator;
}, {} as Record<ConversationLeadStatus, ConversationLeadStatusDefinition>);

const lostStatus: ConversationLeadStatus = 'lost';

export const conversationLossReasonDefinitions: ConversationLossReasonDefinition[] = [
  {
    value: 'delivery_failed',
    label: 'No fue posible contactarlo',
    color: '#991b1b',
    description: 'Todos los mensajes del chatbot fallaron antes de alcanzar al cliente.',
  },
  {
    value: 'no_engagement',
    label: 'Sin contacto efectivo',
    color: '#b91c1c',
    description:
      'El lead nunca respondió y no hay evidencia de que todos los envíos hayan fallado.',
  },
  {
    value: 'abandoned',
    label: 'Abandonó',
    color: '#ea580c',
    description: 'Hubo diálogo inicial, pero luego dejó de responder.',
  },
  {
    value: 'declined',
    label: 'Declinó',
    color: '#7f1d1d',
    description: 'Expresó desinterés o rechazo de forma explícita.',
  },
  {
    value: 'unclassified',
    label: 'Sin clasificar',
    color: '#6b7280',
    description: 'Conversaciones perdidas antiguas o sin razón almacenada.',
  },
];

export const conversationLossReasonMap = conversationLossReasonDefinitions.reduce<
  Record<string, ConversationLossReasonDefinition>
>((accumulator, definition) => {
  accumulator[definition.value] = definition;
  return accumulator;
}, {});

export const isConversationLeadStatus = (
  value: string
): value is ConversationLeadStatus => value in conversationLeadStatusMap;

export const buildConversationLeadStatusFunnelData = (
  leadStatusDistribution: Record<string, number> = {},
  lossReasonDistribution: Record<string, number> = {},
  declineReasonDistribution: Record<string, number> = {}
): ConversationLeadStatusFunnelData => {
  const mainBands = mainFunnelStatuses.map((status) => ({
    ...conversationLeadStatusMap[status],
    exactCount: leadStatusDistribution[status] ?? 0,
    cumulativeCount: leadStatusDistribution[status] ?? 0,
  }));

  let runningTotal = 0;
  const cumulativeMainBands = mainBands
    .slice()
    .reverse()
    .map((definition) => {
      runningTotal += definition.exactCount;
      return {
        ...definition,
        cumulativeCount: runningTotal,
      };
    })
    .reverse();

  const lostExactCount = leadStatusDistribution[lostStatus] ?? 0;
  const knownLostReasonCount = conversationLossReasonDefinitions
    .filter((definition) => definition.value !== 'unclassified')
    .reduce((total, definition) => total + (lossReasonDistribution[definition.value] ?? 0), 0);
  const unclassifiedLostCount = Math.max(0, lostExactCount - knownLostReasonCount);
  const lostReasonBands = conversationLossReasonDefinitions
    .map((definition) => ({
      ...definition,
      count:
        definition.value === 'unclassified'
          ? unclassifiedLostCount
          : lossReasonDistribution[definition.value] ?? 0,
    }))
    .filter((band) => band.count > 0 || band.value !== 'unclassified');
  const declineReasonBands = Object.entries(declineReasonDistribution)
    .filter(([, count]) => count > 0)
    .sort(([, leftCount], [, rightCount]) => rightCount - leftCount)
    .map(([label, count]) => ({ label, count }));

  const nextMainBands = cumulativeMainBands.map((definition, index) => {
    if (index !== 0) {
      return definition;
    }

    return {
      ...definition,
      cumulativeCount: definition.cumulativeCount + lostExactCount,
    };
  });

  return {
    mainBands: nextMainBands,
    sideBand: {
      ...conversationLeadStatusMap[lostStatus],
      exactCount: lostExactCount,
      cumulativeCount: lostExactCount,
    },
    lostReasonBands,
    declineReasonBands,
  };
};

export const getConversationLeadStatusScope = (
  status: ConversationLeadStatus
): ConversationLeadStatus[] => {
  if (status === lostStatus) {
    return [lostStatus];
  }

  const statusIndex = mainFunnelStatuses.indexOf(status);
  return statusIndex === -1 ? [status] : mainFunnelStatuses.slice(statusIndex);
};

export const isConversationLeadStatusWithinScope = (
  status: ConversationLeadStatus,
  selectedStatus?: ConversationLeadStatus
): boolean => selectedStatus ? getConversationLeadStatusScope(selectedStatus).includes(status) : false;

export const getConversationLeadStatusLabel = (status: string): string =>
  isConversationLeadStatus(status) ? conversationLeadStatusMap[status].label : status;

export const getConversationLeadStatusColor = (status: string): string =>
  isConversationLeadStatus(status) ? conversationLeadStatusMap[status].color : '#757575';
